import { Button, Grid2, styled } from '@mui/material';
import FormattedAlias from '@src/components/_shared/Translations/FormattedAlias';
import { isValueInRange } from '@utils/mathHelpers';
import { memo, useMemo, useState } from 'react';
import { useCommandsModalContext } from '../CommandsModalContext';
import CommandsNumberInput from './CommandsNumberInput';

const StyledCommandLabel = styled(Grid2)(() => ({
  display: 'flex',
  justifyContent: 'end',
  alignItems: 'center',
}));

interface Props {
  currentPset: string;
  currentQset: string;
  minPThreshold: number;
  maxPThreshold: number;
  minQThreshold: number;
  maxQThreshold: number;
}

const PowerSection: React.FC<Props> = ({
  currentPset,
  currentQset,
  minPThreshold,
  maxPThreshold,
  minQThreshold,
  maxQThreshold,
}) => {
  const { sendCommand } = useCommandsModalContext();
  const [activePower, setActivePower] = useState<string>('');
  const [reactivePower, setReactivePower] = useState<string>('');

  const isActivePowerInputValid = useMemo(
    () => isValueInRange(activePower, minPThreshold, maxPThreshold),
    [activePower, minPThreshold, maxPThreshold],
  );
  const isReactivePowerInputValid = useMemo(
    () => isValueInRange(reactivePower, minQThreshold, maxQThreshold),
    [reactivePower, minQThreshold, maxQThreshold],
  );

  const handleClickSend = () => {
    if (!isActivePowerInputValid || !isReactivePowerInputValid) {
      return;
    }
    sendCommand('Power', {
      P: activePower,
      Q: reactivePower,
    });
  };

  return (
    <Grid2 container spacing={1}>
      <StyledCommandLabel size={{ xs: 4 }}>
        <FormattedAlias id='Real (P) Command' />:
      </StyledCommandLabel>
      <Grid2 size={{ xs: 8 }}>
        <CommandsNumberInput
          label='Active Power'
          unit='kW'
          minimum={minPThreshold}
          maximum={maxPThreshold}
          value={activePower}
          currentValue={currentPset}
          setValue={setActivePower}
        />
      </Grid2>
      <StyledCommandLabel size={{ xs: 4 }}>
        <FormattedAlias id='Reactive (Q) Command' />:
      </StyledCommandLabel>
      <Grid2 size={{ xs: 8 }}>
        <CommandsNumberInput
          label='Reactive Power'
          unit='kVAR'
          minimum={minQThreshold}
          maximum={maxQThreshold}
          value={reactivePower}
          currentValue={currentQset}
          setValue={setReactivePower}
        />
      </Grid2>
      <Grid2 size={{ xs: 4 }} />
      <Grid2 size={{ xs: 8 }} sx={{ textAlign: 'center' }}>
        <Button
          variant='contained'
          disabled={!isActivePowerInputValid || !isReactivePowerInputValid}
          onClick={handleClickSend}
        >
          <FormattedAlias id='Send' />
        </Button>
      </Grid2>
    </Grid2>
  );
};

PowerSection.displayName = 'PowerSection';

export default memo(PowerSection);
